<template>
  <div v-if="hasPatients" class="mb-5 flex w-fit w-full space-x-5">
    <BaseSwitchButton
      v-show="canAddMorePatients || isExpanded"
      v-model="isExpanded"
      :disabled="isFirstPatientMode"
    />
    <div
      v-show="canAddMorePatients || isExpanded"
      class="flex w-full items-center space-x-2.5"
    >
      <inline-svg
        :src="useAsset(`icons/base/${age}.svg`)"
        loading="lazy"
        class="w-6"
      />

      <div class="w-full">
        <span class="text-sm font-semibold text-primary">
          {{ age === 'kid' ? $t('patients.kid') : $t('patients.adult') }}
        </span>
        <span
          v-if="age === 'kid'"
          class="block w-full text-[10px] text-primary"
          >{{ $t('patients.kid_subtext') }}</span
        >
      </div>
    </div>
  </div>

  <div>
    <PatientsAddForm
      v-for="(patient, index) in agePatients"
      ref="patientsForms"
      :key="patient.id"
      :index="index"
      :patient="patient"
      :can-delete="agePatients.length > 1"
    />

    <button
      v-if="canAddMorePatients && isExpanded && !isFirstPatientMode"
      class="transition-ease-in-out mb-6 mt-3 inline-block cursor-pointer text-sm font-semibold text-primary hover:underline hover:brightness-50"
      @click="onClickAddPatient"
    >
      {{ age === 'kid' ? $t('patients.add_kid') : $t('patients.add_adult') }}
    </button>
  </div>
</template>

<script setup>
import { v4 as uuidv4 } from 'uuid'
import { useAsset } from '~~/composables/asset'
import { useCartStore } from '~~/stores/cart'
import InlineSvg from 'vue-inline-svg'

const {
  public: { baseUrl },
} = useRuntimeConfig()
const route = useRoute()

const loginURL = computed(() => {
  return `login?returnURL=${baseUrl}${route.path}`
})

const patients = computed(() => {
  return cartStore.patients.sort((p1, p2) => {
    if (p1.age < p2.age) return -1
    if (p1.age > p2.age) return 1
    return 0
  })
})

const hasPatients = computed(() => {
  return patients.value.length > 0
})

const emits = defineEmits(['addedPatient'])
const props = defineProps({
  age: {
    type: [String],
    required: true,
    validator(value) {
      return ['kid', 'adult'].includes(value)
    },
  },
  isFirstPatientMode: {
    type: [Boolean],
    default: false,
  },
})

const cartStore = useCartStore()
const isExpanded = ref(false)
const patientsForms = ref([])

const canAddMorePatients = computed(() => {
  return cartStore.patients.length + cartStore.tmpPatients.length < 5
})

const agePatients = computed(() => {
  return cartStore.tmpPatients.filter((item) => item.age.match(props.age))
})

const hasAgePatients = computed(() => {
  return agePatients.value.length > 0
})

const generateEmptyPatientData = () => {
  return {
    id: uuidv4(),
    first_name: '',
    last_name: '',
    pesel: '',
    isNoPesel: false,
    birth_date: '',
    sex: '',
    age: props.age,
    phone_number: '',
    country_code: '',
    country: 'PL',
  }
}

const onClickAddPatient = async () => {
  const isValid = await validate()

  if (isValid) {
    cartStore.pushTmpPatient(generateEmptyPatientData())
  }
}

const validate = async () => {
  let isValid = true

  for (let form of patientsForms.value) {
    if (!(await form.validate())) isValid = false
  }

  return isValid
}

watch(isExpanded, (newValue) => {
  if (newValue) {
    cartStore.pushTmpPatient(generateEmptyPatientData())
    emits('addedPatient')
  } else {
    cartStore.clearTmpPatients(props.age)
  }
})

onMounted(() => {
  isExpanded.value = hasAgePatients.value

  if (props.isFirstPatientMode) {
    cartStore.clearTmpPatients()
    isExpanded.value = true
  }
})

defineExpose({ validate })
</script>
